import { createContext, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useOktaAuth } from '@okta/okta-react'
import { ROUTES } from '@/constants/routes'
import { clearAuth } from '@/utils/auth'

export type AuthContextValue = {
  logout: () => void | Promise<void | unknown>
}

/** Exported for useAuth.ts; use useAuth() or useAuthOptional() in components. */
export const AuthContext = createContext<AuthContextValue | null>(null)

/**
 * Provider for Okta mode (must be used inside Security).
 * Provides logout that calls oktaAuth.signOut().
 */
export function OktaAuthProvider({ children }: { children: React.ReactNode }) {
  const { oktaAuth } = useOktaAuth()
  const logout = useCallback(() => oktaAuth.signOut(), [oktaAuth])
  return <AuthContext.Provider value={{ logout }}>{children}</AuthContext.Provider>
}

/**
 * Provider for legacy email/password mode.
 * Provides logout that clears storage and navigates to login.
 */
export function LegacyAuthProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate()
  const logout = useCallback(() => {
    clearAuth()
    navigate(ROUTES.LOGIN, { replace: true })
  }, [navigate])
  return <AuthContext.Provider value={{ logout }}>{children}</AuthContext.Provider>
}
